import { computed, ref } from 'vue';
import AuthService from '@/modules/user/AuthService';
import processProxy from '@/env/processProxy';
import EnvironmentService from '@/env/EnvironmentService';
import { useLanguageCookie } from '@/composables/useLanguageCookie';
import type { MessageDataObject } from '@/composables/useIframeConnection';
import { useIframeConnection } from '@/composables/useIframeConnection';
import { IFRAME_ACTIONS, iframeBridge } from '@/composables/iframeBridgeProtocol';

const PRELOADED_IFRAME_SETUP_KEY = '__FINCRM_PRELOADED_IFRAME_SETUP__';

type PreloadedModalOptions = {
  maxWidth?: number;
  persistent?: boolean;
  fullscreen?: boolean;
  contentClass?: string;
};

type ChildCapabilities = {
  inPlaceClientCreate?: boolean;
};

const DEFAULT_MODAL_OPTIONS: PreloadedModalOptions = {
  maxWidth: 962,
  persistent: true,
  fullscreen: false,
};

// Module-level (singleton) state: one preloaded iframe per app.
const iframeElement = ref<HTMLIFrameElement | null>(null);
const isReady = ref(false);
const isVisible = ref(false);
const isFullscreen = ref(false);
const isContentLoading = ref(false);
const currentModalName = ref<string | null>(null);
const modalOptions = ref<PreloadedModalOptions>({ ...DEFAULT_MODAL_OPTIONS });
const messageCallbackRef = ref<((eventData: MessageDataObject) => void) | null>(null);
const childCapabilities = ref<ChildCapabilities>({});
const pendingResolve = ref<((data?: any) => void) | null>(null);
const pendingPromise = ref<Promise<any> | null>(null);
let removePersistentListener: (() => void) | null = null;

// Resolves the awaiter of the modal that is being superseded (by a close or a
// retarget) with whatever value it would have received, then clears it.
const resolvePending = (value?: unknown): void => {
  if (pendingResolve.value) {
    pendingResolve.value(value);
    pendingResolve.value = null;
  }
};

// Installs a fresh awaitable for the next modal lifecycle (open or retarget)
// and returns it so callers can hand it straight back to their caller.
const installPendingPromise = (): Promise<any> => {
  pendingPromise.value = new Promise((resolve) => {
    pendingResolve.value = resolve;
  });
  return pendingPromise.value;
};

// CONTENT_READY (page painted) clears the loader. NAVIGATED (route confirm)
// only arms this fallback so an older V3 build that never sends CONTENT_READY
// still clears — see docs/superpowers/specs/2026-07-31-preloaded-iframe-rive-loader-design.md.
const NAVIGATED_FALLBACK_MS = 4000;
let contentLoadingFallback: ReturnType<typeof setTimeout> | null = null;

const clearContentLoadingFallback = () => {
  if (contentLoadingFallback) {
    clearTimeout(contentLoadingFallback);
    contentLoadingFallback = null;
  }
};

const isPreloadedIframeAvailable = computed(() => isReady.value && !isVisible.value);

/**
 * Enriches the openModal query the way GoToService.getLendiV3Url does: the current
 * UI language on every open (V3 otherwise freezes its boot-time locale — see
 * docs/superpowers/specs/2026-07-10-preloaded-iframe-modal-language-sync-design.md),
 * plus auth tokens for Vercel/local where cross-domain cookies are not shared.
 */
function enrichQuery(
  route: string,
  query: Record<string, unknown>,
): Record<string, unknown> {
  const isLocal = processProxy.VUE_APP_ENV === 'DEV';
  const isVercel =
    processProxy.VUE_APP_VERCEL_ENV === 'preview' ||
    EnvironmentService.Environment.isVercelHosted;
  const enriched = { ...query };
  const language = useLanguageCookie().value;
  if (language && !enriched.language) {
    enriched.language = language;
  }
  // Mode first, existence second — mirroring AuthService.token. A user migrated
  // from legacy to Zitadel keeps a stale legacy JWT in localStorage (AuthService
  // even warns about that ambiguity), so branching on `oldToken` alone handed the
  // child the wrong identity in Zitadel mode.
  if (
    AuthService.isZitadelAuthMode &&
    AuthService.accessToken &&
    (isVercel || isLocal) &&
    !enriched.mfaToken
  ) {
    enriched.mfaToken = AuthService.accessToken;
  } else if (
    !AuthService.isZitadelAuthMode &&
    AuthService.oldToken &&
    !route.includes('authToken') &&
    !enriched.authToken
  ) {
    enriched.authToken = AuthService.oldToken;
  }
  // Preloaded-only marker. Legacy `openIframeModal` URLs never carry it, so the
  // child can gate preloaded-only behavior (keeping the wizard open on back) on it.
  enriched.inPlace = 'true';
  return enriched;
}

export const usePreloadedIframe = () => {
  const { sendToIframe, listen } = useIframeConnection();

  const setIframeElement = (element: HTMLIFrameElement | null) => {
    iframeElement.value = element;
    // The element going away takes the child with it, so readiness must follow.
    // Left stale, isPreloadedIframeAvailable keeps advertising a usable iframe
    // and openModal shows a modal it has no way to drive: the OPEN_MODAL send is
    // guarded on the element, but visibility and the loader are not, leaving a
    // spinner that nothing clears on a persistent dialog Escape cannot close.
    // A fresh child announces READY again.
    if (!element) {
      isReady.value = false;
    }
  };

  const sendPing = () => {
    if (!iframeElement.value) return;
    sendToIframe(iframeElement.value, iframeBridge.toLendiCrm.control.ping.create());
  };

  const closeModal = (data?: any) => {
    clearContentLoadingFallback();
    if (iframeElement.value) {
      sendToIframe(iframeElement.value, iframeBridge.toLendiCrm.control.reset.create());
    }
    isVisible.value = false;
    isContentLoading.value = false;
    currentModalName.value = null;
    messageCallbackRef.value = null;
    // The singleton must go back to a clean slate: a child that reported
    // FULLSCREEN_OPEN and then closed without FULLSCREEN_CLOSE would otherwise
    // open the *next* modal fullscreen, and per-modal options would accumulate.
    isFullscreen.value = false;
    modalOptions.value = { ...DEFAULT_MODAL_OPTIONS };
    resolvePending(data);
    pendingPromise.value = null;
  };

  const openModal = (
    name: string,
    route: string,
    params: Record<string, unknown> = {},
    options: PreloadedModalOptions = {},
    messageCallback?: (eventData: MessageDataObject) => void,
    query: Record<string, unknown> = {},
  ): Promise<any> => {
    clearContentLoadingFallback();
    // An awaiter left over from a previous lifecycle would be dropped by the
    // fresh pending promise below and never settle — hand it the same
    // "superseded" resolution a retarget gives.
    resolvePending(undefined);
    modalOptions.value = { ...DEFAULT_MODAL_OPTIONS, ...options };
    currentModalName.value = name;
    isVisible.value = true;
    isContentLoading.value = true;
    messageCallbackRef.value = messageCallback || null;

    if (iframeElement.value) {
      sendToIframe(
        iframeElement.value,
        iframeBridge.toLendiCrm.control.openModal.create({
          route,
          query: enrichQuery(route, query),
          params,
        }),
      );
    }

    return installPendingPromise();
  };

  /**
   * Swaps the open modal's identity without closing it: the superseded awaiter
   * is resolved with `resolveCurrentWith` — the same value a close would have
   * delivered — and a fresh pending promise is installed under the new name.
   * The iframe never parks, so no loader and no remount.
   */
  const retargetModal = (
    name: string,
    messageCallback?: (eventData: MessageDataObject) => void,
    resolveCurrentWith?: unknown,
  ): Promise<any> => {
    if (!isVisible.value) {
      return Promise.resolve(undefined);
    }
    resolvePending(resolveCurrentWith);
    currentModalName.value = name;
    messageCallbackRef.value = messageCallback || null;

    return installPendingPromise();
  };

  const handleMessage = (eventData: MessageDataObject) => {
    if (iframeBridge.toFincrm.control.ready.matches(eventData)) {
      isReady.value = true;
      // A child that advertises nothing (older build) resets us to no capabilities.
      childCapabilities.value = eventData.data?.capabilities ?? {};
      return;
    }
    if (iframeBridge.toFincrm.control.navigated.matches(eventData)) {
      if (isContentLoading.value) {
        clearContentLoadingFallback();
        contentLoadingFallback = setTimeout(() => {
          isContentLoading.value = false;
          contentLoadingFallback = null;
        }, NAVIGATED_FALLBACK_MS);
      }
      return;
    }
    if (iframeBridge.toFincrm.control.contentReady.matches(eventData)) {
      clearContentLoadingFallback();
      isContentLoading.value = false;
      return;
    }
    if (iframeBridge.infra.iframeUi.fullscreenOpen.matches(eventData)) {
      isFullscreen.value = true;
      return;
    }
    if (iframeBridge.infra.iframeUi.fullscreenClose.matches(eventData)) {
      isFullscreen.value = false;
      return;
    }
    if (!currentModalName.value || eventData.type !== currentModalName.value) {
      return;
    }
    if (eventData.action === IFRAME_ACTIONS.CLOSE_MODAL) {
      closeModal(eventData.data);
      return;
    }
    if (messageCallbackRef.value) {
      messageCallbackRef.value(eventData);
    }
  };

  /**
   * Registers the single persistent listener. Call from main.ts (outside a
   * component) so getCurrentInstance() is null and the listener is never
   * auto-removed — READY can never be missed.
   */
  const setup = () => {
    const globalWindow = window as Window & { [PRELOADED_IFRAME_SETUP_KEY]?: boolean };
    if (globalWindow[PRELOADED_IFRAME_SETUP_KEY] || removePersistentListener) {
      return;
    }
    globalWindow[PRELOADED_IFRAME_SETUP_KEY] = true;

    removePersistentListener = listen((eventData, rawEvent) => {
      if (!eventData?.type || !rawEvent) {
        return;
      }
      const targetContentWindow = iframeElement.value?.contentWindow;
      if (targetContentWindow) {
        // Bind strictly to our preloaded iframe's window (origin alone cannot
        // distinguish two same-origin iframes).
        if (rawEvent.source !== targetContentWindow) {
          return;
        }
      } else {
        // Element ref not wired yet: accept only an early READY so the handshake
        // is never missed; reject everything else.
        if (!iframeBridge.toFincrm.control.ready.matches(eventData)) {
          return;
        }
      }
      handleMessage(eventData);
    });
  };

  return {
    iframeElement,
    isReady,
    isPreloadedIframeAvailable,
    isVisible,
    isFullscreen,
    isContentLoading,
    currentModalName,
    childCapabilities,
    modalOptions,
    setIframeElement,
    sendPing,
    openModal,
    retargetModal,
    closeModal,
    setup,
  };
};
